import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";

interface StoredDraft<T> { revision: number; value: T }
export class SpreadsheetDraftConflict extends Error {
  constructor() { super("This spreadsheet was changed in another tab. Reopen it here to load the latest progress."); }
}
async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("listwork-spreadsheets", 1);
    let ended = false;
    const timer = setTimeout(() => { ended = true; reject(new Error("Draft storage did not respond. Reopen this page and try again.")); }, 5000);
    request.onupgradeneeded = () => request.result.createObjectStore("drafts");
    request.onsuccess = () => { clearTimeout(timer); if (ended) request.result.close(); else resolve(request.result); };
    request.onerror = request.onblocked = () => { clearTimeout(timer); ended = true; reject(new Error("Draft storage is unavailable. Allow this site to store data, then reopen this page.")); };
  });
}
export async function readSpreadsheetDraft<T>(key: string): Promise<StoredDraft<T> | undefined> {
  const db = await database();
  try { return await new Promise((resolve, reject) => {
    const transaction = db.transaction("drafts", "readonly"), request = transaction.objectStore("drafts").get(key);
    let result: StoredDraft<T> | undefined;
    request.onsuccess = () => { result = request.result; };
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = transaction.onabort = () => reject(new Error("Could not read the saved spreadsheet. Reopen this page and try again."));
  }); } finally { db.close(); }
}
export async function writeSpreadsheetDraft<T>(key: string, revision: number, value: T): Promise<number> {
  const db = await database();
  try { return await new Promise((resolve, reject) => {
    const transaction = db.transaction("drafts", "readwrite"), store = transaction.objectStore("drafts"), request = store.get(key);
    let conflict = false;
    request.onsuccess = () => {
      if ((request.result?.revision ?? 0) !== revision) { conflict = true; transaction.abort(); return; }
      store.put({ revision: revision + 1, value }, key);
    };
    transaction.oncomplete = () => resolve(revision + 1);
    transaction.onerror = transaction.onabort = () => reject(conflict ? new SpreadsheetDraftConflict() : new Error("Your latest progress could not be stored. Keep this page open and try saving again."));
  }); } finally { db.close(); }
}

/** The journal is flushed before every mutation; concurrent tabs use revision checks. */
export function useSpreadsheetDraft<T>(key: string, initial: () => T, accepts: (value: unknown) => value is T) {
  const [value, setState] = useState(initial), current = useRef(value);
  const [ready, setReady] = useState(false), [error, setError] = useState<string>(), [dirty, setDirty] = useState(false);
  const [invalidated, setInvalidated] = useState(false);
  const revision = useRef(0), localRevision = useRef(0), storedRevision = useRef(0);
  const chain = useRef(Promise.resolve()), active = useRef(true), loaded = useRef(false);
  const setValue = useCallback((action: SetStateAction<T>) => {
    if (!loaded.current) return;
    current.current = typeof action === "function" ? (action as (previous: T) => T)(current.current) : action;
    localRevision.current++; setState(current.current); setDirty(true);
  }, []);
  const flush = useCallback(async () => {
    if (!loaded.current) throw new Error("Wait for your saved spreadsheet to load.");
    const snapshot = current.current, version = localRevision.current;
    let failure: unknown;
    const next = chain.current.catch(() => undefined).then(async () => {
      if (version <= storedRevision.current) return;
      try {
        revision.current = await writeSpreadsheetDraft(key, revision.current, snapshot);
        storedRevision.current = version;
        if (active.current) { setError(undefined); setDirty(localRevision.current > version); }
      } catch (caught) {
        failure = caught;
        if (active.current) { setError(caught instanceof Error ? caught.message : "Could not save your progress."); if (caught instanceof SpreadsheetDraftConflict) setInvalidated(true); }
      }
    });
    chain.current = next; await next; if (failure) throw failure;
  }, [key]);
  useEffect(() => {
    let cancelled = false;
    active.current = true;
    void readSpreadsheetDraft<T>(key).then((stored) => {
      if (cancelled) return;
      if (stored) {
        if (!Number.isSafeInteger(stored.revision) || stored.revision < 1 || !accepts(stored.value)) throw new Error("This saved spreadsheet could not be read. Keep it on this device and contact support.");
        revision.current = stored.revision; current.current = stored.value; setState(stored.value);
      }
      loaded.current = true; setReady(true);
    }).catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "Could not load your spreadsheet."); });
    return () => { cancelled = true; active.current = false; if (loaded.current) void flush().catch(() => undefined); };
  }, [key, flush]);
  useEffect(() => { if (!ready || !dirty || invalidated) return; const timer = setTimeout(() => { void flush().catch(() => undefined); }, 350); return () => clearTimeout(timer); }, [value, ready, dirty, invalidated, flush]);
  useEffect(() => {
    const protect = (event: BeforeUnloadEvent) => { if (localRevision.current > storedRevision.current) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", protect);
    return () => window.removeEventListener("beforeunload", protect);
  }, []);
  return { value, setValue, current, ready, error, dirty, invalidated, flush };
}
